export const extractDetails = (json: any): Record<string, any> => {
  if (!json) return {};
  if (typeof json === 'string') {
    try {
      return extractDetails(JSON.parse(json));
    } catch {
      return {};
    }
  }

  const dataPart = json.data || {};
  const merged = {
    ...json,
    ...dataPart,
    ...(dataPart.fields || {}),
    ...(dataPart.details || {}),
    ...(json.fields || {}),
    ...(json.details || {}),
  };

  if (json.title) merged.title = json.title;
  if (json.image) merged.image = json.image;
  if (json.certImage) merged.certImage = json.certImage;
  if (json.images) merged.images = json.images;

  // Normalize known MBA aliases from older payloads.
  if (merged['M B A Grade'] && !merged['MBA Grade']) {
    merged['MBA Grade'] = merged['M B A Grade'];
  }
  if (merged['M B A Serial Number'] && !merged['MBA Serial Number']) {
    merged['MBA Serial Number'] = merged['M B A Serial Number'];
  }
  if (merged['M B A Grade']) delete merged['M B A Grade'];
  if (merged['M B A Serial Number']) delete merged['M B A Serial Number'];

  // CGC Cards: newer cert pages parsed as "Grade: Date 2025" (the grading year
  // under the wrong label) with the real grade only in the population line,
  // "In MINT+ 9.5 Grade: 73". The parser and stored scans are fixed; this keeps
  // any older copy readable.
  if (typeof merged.Grade === 'string' && /^(?:date\b|\d{4}(?:-\d{2}(?:-\d{2})?)?$)/i.test(merged.Grade.trim())) {
    const gradeLine = merged.populationSummary?.gradeLine ?? merged.population?.gradeLine;
    const fromPopulation = String(gradeLine ?? '').match(/In\s+(.+?)\s+Grade/i)?.[1]?.trim();
    if (!merged['Grade Date']) merged['Grade Date'] = merged.Grade.replace(/^date\s*/i, '').trim();
    if (fromPopulation) merged.Grade = fromPopulation;
    else delete merged.Grade;
    if (typeof merged.grade === 'string' && /^date\b/i.test(merged.grade)) merged.grade = merged.Grade;
  }

  return merged;
};

const decodeHtmlEntities = (text: string) => {
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&#x27;': "'",
    '&apos;': "'",
  };
  return text.replace(/&[#\w]+;/g, (entity) => {
    if (entities[entity]) return entities[entity];
    if (entity.startsWith('&#x')) return String.fromCharCode(parseInt(entity.slice(3, -1), 16));
    if (entity.startsWith('&#')) return String.fromCharCode(parseInt(entity.slice(2, -1), 10));
    return entity;
  });
};

export const formatDisplayText = (value: any) => {
  if (typeof value !== 'string') return value;
  let text = value;
  text = text.replace(/<!--[\s\S]*?-->/g, ' ');
  text = text.replace(/<\s*br\s*\/?>/gi, '\n');
  text = text.replace(/<\s*\/?p\s*>/gi, '\n');
  text = text.replace(/<[^>]+>/g, '');
  text = decodeHtmlEntities(text);
  text = text.replace(/\r\n/g, '\n');
  text = text.replace(/[ \t]+\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
};

const isPsaTechnologyIconImageUrl = (url: string) =>
  /(?:^|\/)table-image-[^/?#]+\.(?:png|webp|jpe?g)(?:[?#].*)?$/i.test(url);

const isBlockedImageUrl = (url: string) =>
  url.startsWith('file://') || isPsaTechnologyIconImageUrl(url);

export const sanitizeImageUrl = (value: any) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isBlockedImageUrl(trimmed)) return null;
  return trimmed;
};

const extractImageUrl = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') return sanitizeImageUrl(value);
  if (typeof value === 'object') {
    return (
      sanitizeImageUrl(value.url) ||
      sanitizeImageUrl(value.image) ||
      sanitizeImageUrl(value.href) ||
      null
    );
  }
  return null;
};

const extractFromArray = (value: any) => {
  if (!Array.isArray(value)) return null;
  for (const entry of value) {
    const url = extractImageUrl(entry);
    if (url) return url;
  }
  return null;
};

/**
 * A moderated genuine-item photo is the most useful thumbnail for a cleared
 * certificate.  History, Collections and Wishlist all pass their saved worker
 * response through this module, so resolving it here keeps list and Gallery
 * views in sync without changing the saved scan row.
 */
export const resolveVerifiedItemImageUrl = (details: any) => {
  const verified =
    details?.verifiedException ||
    details?.verified_exception ||
    details?.data?.verifiedException ||
    details?.data?.verified_exception ||
    null;

  return (
    extractImageUrl(verified?.image) ||
    extractImageUrl(verified?.imageUrl) ||
    extractImageUrl(verified?.photo) ||
    extractImageUrl(verified?.photoUrl) ||
    null
  );
};

export const resolvePrimaryImageUrl = (details: any) => {
  return (
    extractImageUrl(details?.image) ||
    extractImageUrl(details?.imageUrl) ||
    extractImageUrl(details?.hologramCheck?.imageUrl) ||
    extractImageUrl(details?.hologramCheck?.image) ||
    extractFromArray(details?.images) ||
    extractFromArray(details?.imageUrls) ||
    extractImageUrl(details?.primaryRemoteImageUrl) ||
    extractFromArray(details?.remoteImageUrls) ||
    extractImageUrl(details?._historyMeta?.primaryRemoteImageUrl) ||
    extractFromArray(details?._historyMeta?.remoteImageUrls) ||
    extractFromArray(details?.metadata?.images) ||
    extractFromArray(details?.metadata?.imageUrls) ||
    extractImageUrl(details?.metadata?.imageFrontUrl) ||
    extractImageUrl(details?.metadata?.imageBackUrl) ||
    extractImageUrl(details?.metadata?.frontImageUrl) ||
    extractImageUrl(details?.metadata?.front_image_url) ||
    extractImageUrl(details?.raw?.front_image_url) ||
    extractImageUrl(details?.raw?.front_image) ||
    extractImageUrl(details?.raw?.image_url) ||
    extractImageUrl(details?.raw?.imageUrl) ||
    extractImageUrl(details?.raw?.card_image) ||
    null
  );
};

export const shouldHideInternalDetailKey = (key: string | null | undefined) => {
  if (!key) return true;
  return key === 'item_type';
};

export const deriveCertificateItemType = ({
  companyId,
  workerImageType,
  hasImages,
  isGradedCard,
}: {
  companyId?: string | null;
  workerImageType?: string | null;
  hasImages?: boolean;
  isGradedCard?: boolean;
}) => {
  const company = String(companyId || '').toUpperCase();
  const imageType = String(workerImageType || '').toUpperCase();
  // Companies whose autograph certs are LOA by default. BECKETT is intentionally
  // NOT here: a base Beckett witnessed cert (no photo on file) is a COA — only an
  // upgraded Beckett cert with an image is an LOA. That distinction is driven by
  // `hasImages` / an explicit LOA imageType below, matching the mobile deriveItemType.
  const loaCompanies = new Set(['PSA_DNA', 'SPENCE', 'PSA_LOA']);
  const alwaysCoa = new Set(['GOAT', 'GOATAUTHENTICS', 'G.O.A.T. AUTHENTICS', 'CA', 'CELEBRITY_AUTHENTICS', 'SWAU', 'ONLINEAUTHENTICS', 'ACOA']);
  const isGradingCompany =
    ['PSA_CARD', 'PSA_VIDEO_GAMES', 'BECKETT_GRADING', 'SGC_GRADING', 'SGC', 'SGC_AUTHENTIC', 'TAG', 'TAG_GRADING', 'AMG', 'AMG_GRADING'].includes(company) ||
    company.startsWith('CGC');

  if (isGradedCard || isGradingCompany) {
    return imageType.startsWith('LOA') || loaCompanies.has(company) ? 'LOA' : 'Graded Card';
  }

  if (alwaysCoa.has(company)) return 'COA';
  if (imageType.startsWith('COA')) return 'COA';
  if (imageType.startsWith('LOA') || loaCompanies.has(company) || hasImages) return 'LOA';
  return 'COA';
};

// Canonical client-side fraud predicate — mirrors the SQL scan_is_fraud()
// (public.scan_is_fraud in Supabase). A scan is fraud when a fraudAlert object
// is present at either the top level or nested under `data` (current + legacy
// key spellings), OR possibly_status is set to anything other than 'Clean'.
// Checking only the top-level fraudAlert undercounts fraud (web-manual scans
// nest it under `data`), which is what broke the dashboard fraud charts.
const isFraudAlertValue = (value: any): boolean => {
  if (typeof value === 'string') return value.trim().length > 0;
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

export const isScanFraud = (scan: any): boolean => {
  if (!scan) return false;

  const status = String(scan.possibly_status || '').trim();
  if (status && !/^clean$/i.test(status)) return true;

  const details = scan.details_json && typeof scan.details_json === 'object' ? scan.details_json : {};
  const data = details.data && typeof details.data === 'object' ? details.data : {};

  return (
    isFraudAlertValue(details.fraudAlert) ||
    isFraudAlertValue(details.fraud_alert) ||
    isFraudAlertValue(data.fraudAlert) ||
    isFraudAlertValue(data.fraud_alert)
  );
};

const formatPaniniCertificateNumber = (value: string) => {
  const trimmed = value.trim().toUpperCase();
  if (!trimmed) return '';
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  const prefixMatch = trimmed.match(/^[A-Z]+/);
  const prefix = prefixMatch ? prefixMatch[0] : 'PA';
  return `${prefix}${digits}`;
};

const formatCertificateNumber = (company: string | null | undefined, value: any) => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text) return '';
  const upperCompany = (company || '').toString().toUpperCase();
  if (upperCompany === 'PANINI') {
    return formatPaniniCertificateNumber(text);
  }
  return text;
};

export const resolveCertificateNumber = (
  company: string | null | undefined,
  scanValue: any,
  details: any
) => {
  const raw =
    scanValue ||
    details?.certificateNumber ||
    details?.certNumber ||
    details?.certificationNumber ||
    details?.hologramNumber ||
    details?.code ||
    '';
  return formatCertificateNumber(company, raw);
};

export const normalizeMbaGradeText = (value: any): string => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s*Key\s*Metrics\s*$/i, '').replace(/\s+/g, ' ').trim();
};

export const extractMbaGradeCode = (value: any): string | null => {
  const grade = normalizeMbaGradeText(value);
  if (!grade) return null;

  const paren = grade.match(/\(([A-Z][A-Z+\-]*)\)/i);
  if (paren?.[1]) return paren[1].toUpperCase();

  const codes = [
    'NM-MT+',
    'NM-MT',
    'EX-MT+',
    'EX-MT',
    'VG-EX+',
    'VG-EX',
    'MT+',
    'NM+',
    'EX+',
    'VG+',
    'GD+',
    'FR+',
    'FL',
    'PR',
    'GM',
    'MT',
    'NM',
    'EX',
    'VG',
    'GD',
    'FR',
    'AU',
    'NH',
  ];

  for (const code of codes) {
    if (new RegExp(`(^|\\W)${code.replace('+', '\\+')}(\\W|$)`, 'i').test(grade)) {
      return code;
    }
  }
  return null;
};

export const extractMbaGradeDisplay = (value: any): string => {
  const grade = normalizeMbaGradeText(value);
  if (!grade) return '';
  const numeric = grade.match(/(^|[^\d])(\d+(?:\.\d+)?)(?=$|[^\d])/);
  const code = extractMbaGradeCode(grade);
  if (numeric?.[2] && code) return `${numeric[2]} ${code}`;
  if (numeric?.[2]) return numeric[2];
  if (code) return code;
  return grade;
};

export const formatGradeForOverlay = (company: string | null | undefined, grade: any, details?: any): string => {
  const upperCompany = (company || '').toString().toUpperCase();
  let value = grade;
  if (typeof value === 'object' && value !== null) {
    value = (value as any).final || (value as any)['Final Grade'];
  }
  let formatted = value ? String(value).replace(/^PSA\s?|^BGS\s?|^SGC\s?/, '') : '';
  if (formatted.includes('N0: AUTHENTIC')) {
    return 'Authentic';
  }
  if (upperCompany === 'MBA' || upperCompany === 'MBA_GRADING') {
    const mbaGrade = details?.['MBA Grade'] || details?.['M B A Grade'] || formatted;
    return extractMbaGradeDisplay(mbaGrade);
  }
  return formatted;
};
